import { useNotifications, WsNotification } from "@/app/notifications";
import { useUnreadChatCount } from "@stockline/web";
import { useUserOptional } from "@/components/auth-provider";
import { Button } from "@stockline/ui/components/button";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  useSidebar,
} from "@/components/shadcn/sidebar";
import { MobileAgentNav } from "@mobile/components/MobileAgentNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebarWidth } from "@/hooks/use-sidebar-width";
import { useGlobalState } from "@/lib/states";
import { TRANSPORT_URL_MAP } from "@/lib/default-route";
import { useFeatureFlag, useFeatureFlags } from "@/query/hooks/useFeatureFlags";
import { useUnreadSteeringTabCount } from "@/query/hooks/useBi";
import clsx from "clsx";
import { Home, X, MapPin, Truck, type LucideProps } from "lucide-react";
import { SidebarFeedbackButton } from "@/app/feedback/components/SidebarFeedbackButton";
import { Link } from "react-router-dom";
import { usePathname } from "@/hooks/use-pathname-compat";
import { useSearchParams } from "@/hooks/use-search-params-compat";
import { useTranslation } from "react-i18next";

import {
  type SidebarItem,
  allSidebarItems,
  isSidebarItemEnabled,
  TRANSPORT_TITLE_KEYS,
} from "@/components/sidebar-items";
import { useMyGroups } from "@/query/hooks/useGroups";
import { isGroupAdmin } from "@/lib/group-admin";

// Hook to get filtered sidebar items based on feature flags and permissions
function useFilteredSidebarItems(userPermissionLevel: number): {
  items: SidebarItem[];
  isLoading: boolean;
} {
  const { data: featureFlags, isLoading } = useFeatureFlags();

  // Level 0 users can only see parameters
  if (userPermissionLevel === 0) {
    return {
      items: allSidebarItems.filter((item) => item.id === "parameters"),
      isLoading: false,
    };
  }

  // While loading, return empty array to prevent flash of all menu items
  if (isLoading || !featureFlags) {
    return { items: [], isLoading: true };
  }

  // Filter items based on feature flags. Home is gated opt-in so it stays off
  // until a company turns it on, matching the /home route gate (see
  // app/home/gate.tsx). See isSidebarItemEnabled for the opt-in/opt-out rules.
  const filtered = allSidebarItems.filter((item) =>
    isSidebarItemEnabled(item, featureFlags),
  );

  // In transport mode, swap titleKeys and icons for affected items
  const transportKeyMap = TRANSPORT_TITLE_KEYS;

  const transportIconMap: Record<string, React.ComponentType<LucideProps>> = {
    products: MapPin,
    purchases: Truck,
    recap: Truck,
  };

  // In transport mode, also swap URLs for affected items. Shared with the `/`
  // dispatcher (see lib/default-route.ts) so both remap recap → /recap_transport
  // from one source of truth.
  const transportUrlMap = TRANSPORT_URL_MAP;

  const items =
    featureFlags.transportMode === true
      ? filtered.map((item) =>
          transportKeyMap[item.id]
            ? {
                ...item,
                titleKey: transportKeyMap[item.id],
                ...(transportIconMap[item.id] && {
                  icon: transportIconMap[item.id],
                }),
                ...(transportUrlMap[item.id] && {
                  url: transportUrlMap[item.id],
                }),
              }
            : item,
        )
      : filtered;

  return { items, isLoading: false };
}

// Sidebar Header Component with Logo
function SidebarHeaderWithLogo({ onClose }: { onClose?: () => void }) {
  // The logo only routes to /home when home is enabled; otherwise it's inert
  // (no navigation), so a click never bounces through the disabled-home redirect.
  const homeEnabled = useFeatureFlag("sidebarShowHome") === true;
  const logoClassName =
    "flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-colors group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center";
  const logoContent = (
    <>
      <img
        src="/logo.webp"
        alt="Stockline"
        className="w-8 h-8 aspect-square shrink-0 rounded-lg shadow-sm object-contain select-none group-data-[collapsible=icon]:size-8"
        draggable={false}
      />
      <span className="font-bold text-sm text-sidebar-foreground truncate group-data-[collapsible=icon]:hidden">
        Stockline
      </span>
    </>
  );

  return (
    <SidebarHeader className="pb-8 group-data-[collapsible=icon]:p-1 group-data-[collapsible=icon]:pb-8">
      <div className="flex items-center justify-between p-2 group-data-[collapsible=icon]:p-0">
        {/* Stockline Logo - 32px expanded with label; large standalone logo when collapsed */}
        {homeEnabled ? (
          <Link
            to="/home"
            className={`${logoClassName} hover:bg-sidebar-accent hover:text-sidebar-accent-foreground`}
          >
            {logoContent}
          </Link>
        ) : (
          <div className={`${logoClassName} cursor-default`}>{logoContent}</div>
        )}
        {/* Close Button - hidden when collapsed */}
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 group-data-[collapsible=icon]:hidden"
          >
            <X size={16} />
          </Button>
        )}
      </div>
    </SidebarHeader>
  );
}

// Desktop Sidebar Skeleton
function DesktopSidebarSkeleton({ onClose }: { onClose?: () => void }) {
  return (
    <>
      <SidebarHeaderWithLogo onClose={onClose} />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SidebarMenuItem key={i}>
                <div className="flex items-center gap-2 px-2 py-2">
                  <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
                  <div className="w-20 h-4 rounded bg-gray-200 animate-pulse group-data-[collapsible=icon]:hidden" />
                </div>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </>
  );
}

// Desktop Sidebar Content Component
function DesktopSidebarContent({
  sidebarItems,
  pathname,
  user,
  t,
  notificationsMap,
  onClose,
  isLoading,
}: {
  sidebarItems: SidebarItem[];
  pathname: string;
  user: any;
  t: any;
  notificationsMap: Record<string, WsNotification[]>;
  onClose?: () => void;
  isLoading?: boolean;
}) {
  // Reactive AI-inbox unread count for the AI nav item's violet badge. The hook
  // reads offline data and self-defaults to 0 when the store isn't mounted, so
  // it's safe to call here unconditionally (rules of hooks: before any return).
  const aiUnreadCount = useUnreadChatCount();
  // Unread-tab count for the steering nav item's violet badge (STK-1658): one
  // per dashboard whose tab is lit — by unseen widgets OR an unread BI brief —
  // mirroring the tab strip. Unions the board state (polled tab list) with the
  // brief state (chat-session `unread`); a tab lit by both still counts once.
  const biTabCount = useUnreadSteeringTabCount();

  // The "Groups" item is flag-gated in the item list, but the flag alone must
  // not expose it to a plain member — only to users who actually administer a
  // group (ADMIN / ADMIN_AND_HR) or a company owner. Fetch the caller's groups
  // only when the item is present so non-group companies pay nothing.
  const hasGroupsItem =
    Array.isArray(sidebarItems) &&
    sidebarItems.some((item) => item.id === "groups");
  const { data: myGroups } = useMyGroups({ enabled: hasGroupsItem });
  const isCompanyOwner = user?.company_permissions === 1;
  const canManageAnyGroup = isCompanyOwner || isGroupAdmin(myGroups, user?.id);

  if (isLoading || !Array.isArray(sidebarItems)) {
    return <DesktopSidebarSkeleton onClose={onClose} />;
  }

  return (
    <>
      <SidebarHeaderWithLogo onClose={onClose} />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {sidebarItems.map((item) => {
              if (item.id === "products" && (!user || user.type !== 2)) {
                return null;
              }
              if (
                item.id === "companyManagement" &&
                (!user || user.company_permissions !== 1)
              ) {
                return null;
              }
              if (item.id === "groups" && !canManageAnyGroup) {
                return null;
              }
              const isActive =
                pathname === item.url ||
                (item.url !== "/" && pathname.startsWith(item.url));
              const notificationCount =
                item.notification &&
                notificationsMap[item.notification] &&
                notificationsMap[item.notification].length > 0
                  ? notificationsMap[item.notification].length
                  : 0;
              // AI + steering nav items carry a violet unread badge, capped at
              // 99+ (AI-inbox unread / unread BI tabs). Other items keep their
              // existing (red) notification bubble.
              let aiBadgeCount = 0;
              if (item.id === "ai") aiBadgeCount = aiUnreadCount;
              else if (item.id === "steering") aiBadgeCount = biTabCount;
              const aiBadgeLabel = aiBadgeCount > 99 ? "99+" : aiBadgeCount;
              const aiBadgeAriaLabel =
                item.id === "steering"
                  ? t(
                      "businessIntelligence.notifications.unreadDashboardsBadge",
                      {
                        count: aiBadgeCount,
                      },
                    )
                  : t("common.aiManagement.unreadBadge", {
                      count: aiBadgeCount,
                    });

              return (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    tooltip={t(item.titleKey)}
                    className={clsx(
                      isActive && "border border-green-200/30 bg-green-50/30",
                      // The collapsed unread bubble sits outside the icon box;
                      // the button clips by default, so let it overflow.
                      aiBadgeCount > 0 && "overflow-visible",
                    )}
                  >
                    <Link
                      to={item.url}
                      className="relative group-data-[collapsible=icon]:justify-center"
                      data-testid={`sidebar-link-${item.id}`}
                      data-attr={`sidebar-link-${item.id}`}
                    >
                      <div className="relative shrink-0">
                        <item.icon size={20} />
                        {/* Small bubble for collapsed state */}
                        {notificationCount > 0 && (
                          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow group-data-[collapsible=icon]:flex hidden">
                            {notificationCount}
                          </span>
                        )}
                        {/* Violet AI-inbox bubble (collapsed state) */}
                        {aiBadgeCount > 0 && (
                          <span
                            className="absolute -top-1.5 -right-1.5 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-500 px-1 text-[10px] font-bold text-white shadow group-data-[collapsible=icon]:flex hidden"
                            aria-label={aiBadgeAriaLabel}
                          >
                            {aiBadgeLabel}
                          </span>
                        )}
                      </div>
                      <span className="group-data-[collapsible=icon]:hidden">
                        {t(item.titleKey)}
                      </span>
                      {/* Regular bubble for expanded state */}
                      {notificationCount > 0 && (
                        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold text-white shadow group-data-[collapsible=icon]:hidden">
                          {notificationCount}
                        </span>
                      )}
                      {/* Violet AI-inbox badge (expanded state) */}
                      {aiBadgeCount > 0 && (
                        <span
                          className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-500 px-1.5 text-[11px] font-bold text-white shadow group-data-[collapsible=icon]:hidden"
                          aria-label={aiBadgeAriaLabel}
                          data-testid={`sidebar-badge-${item.id}`}
                        >
                          {aiBadgeLabel}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>

        {/* Pinned to the bottom of the sidebar — direct line to the dev team.
            Click opens a dropdown with two paths: quick feedback (modal, no
            navigation, captures the page behind it) or the full inbox. */}
        <SidebarGroup className="mt-auto border-t pt-2">
          <SidebarMenu>
            <SidebarFeedbackButton
              isActive={pathname.startsWith("/feedback")}
            />
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </>
  );
}

// Component that uses sidebar context
function SidebarResizeHandle({
  onMouseDown,
}: {
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  const { state, isMobile } = useSidebar();
  // Hide handle when sidebar is collapsed (icon-only) or on mobile.
  if (state === "collapsed" || isMobile) return null;
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      onMouseDown={onMouseDown}
      className="fixed inset-y-0 left-[--sidebar-width] z-40 hidden md:block w-1 -translate-x-1/2 cursor-col-resize group"
    >
      <div className="h-full w-px mx-auto bg-border/0 group-hover:bg-border/60 transition-colors" />
    </div>
  );
}

function SidebarContentWrapper({
  children,
  sidebarItems,
  pathname,
  user,
  t,
  notificationsMap,
  isLoading,
  startResize,
}: {
  children: React.ReactNode;
  sidebarItems: SidebarItem[];
  pathname: string;
  user: any;
  t: any;
  notificationsMap: Record<string, WsNotification[]>;
  isLoading?: boolean;
  startResize: (e: React.MouseEvent) => void;
}) {
  const { toggleSidebar } = useSidebar();

  return (
    <>
      <Sidebar collapsible="icon">
        <DesktopSidebarContent
          sidebarItems={sidebarItems}
          pathname={pathname}
          user={user}
          t={t}
          notificationsMap={notificationsMap}
          onClose={toggleSidebar}
          isLoading={isLoading}
        />
        <SidebarRail />
      </Sidebar>
      <SidebarResizeHandle onMouseDown={startResize} />
      <SidebarInset className="!min-h-0 min-w-0 flex flex-col">
        {children}
      </SidebarInset>
    </>
  );
}

export function AppSidebar({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const isMobile = useIsMobile();
  // The lean Agent app is active on Capacitor (always) and on the website/PWA at
  // a phone-sized viewport. When active we render the focused Agent bottom nav;
  // otherwise the full desktop left sidebar. This matches the runtime router
  // choice in src/router/index.ts, so the nav never links to routes the active
  // router lacks. Split into two shells so the desktop-only sidebar/notification
  // queries never mount on the lean path (especially Capacitor, always mobile).
  const mobileApp = __TARGET__ === "capacitor" || isMobile;
  return mobileApp ? (
    <MobileAppShell>{children}</MobileAppShell>
  ) : (
    <DesktopAppSidebar>{children}</DesktopAppSidebar>
  );
}

function MobileAppShell({ children }: Readonly<{ children: React.ReactNode }>) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const chatId = searchParams.get("c");
  const [globalSelectedChat] = useGlobalState("selected_chat", null);

  // Hide the sidebar/bottom-nav for full-screen mobile surfaces: an active chat
  // conversation (legacy /chat?c=, or the /ai/:sessionId thread route) or an
  // open AI-proposal review (/ai-proposals/:id route).
  const isChatOpen =
    (pathname.startsWith("/chat") &&
      (chatId !== null || globalSelectedChat !== null)) ||
    /^\/ai\/[^/]+/.test(pathname) ||
    /^\/ai-proposals\/[^/]+/.test(pathname);

  // The bottom nav is hidden inside a full-screen chat conversation / proposal
  // review (isChatOpen), but the tree shape stays IDENTICAL either way —
  // {children} is always the flex-1 child and the nav is just a conditional
  // sibling. If the structure differed (e.g. {children} directly in the column
  // when the nav is hidden), navigating list→detail would relocate {children}
  // and React would remount react-router-animated mounted inside it, resetting
  // its prevPath ref and killing the slide.
  // The nav is a STATIC flex child (not fixed): the content slot shrinks by
  // the nav's height, so page bottoms (last list row, account disconnect)
  // can never be hidden underneath it.
  // Use dynamic viewport height (dvh), not vh: on mobile browsers `100vh` is the
  // LARGEST viewport (address bar retracted), so while the bar is showing the
  // bottom of this column — the chat composer — sits below the visible fold and
  // is only revealed by scrolling. `dvh` tracks the current visible viewport, so
  // the composer stays pinned to the real bottom edge.
  return (
    <div className="flex flex-col h-dvh max-h-dvh">
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
      {!isChatOpen && <MobileAgentNav />}
    </div>
  );
}

function DesktopAppSidebar({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const pathname = usePathname();
  const user = useUserOptional();
  const { t } = useTranslation();
  const userPermissionLevel = user?.feature_permissions ?? 0;
  const { items: sidebarItems, isLoading: isSidebarLoading } =
    useFilteredSidebarItems(userPermissionLevel);

  const [orders] = useNotifications("order");
  const [contacts] = useNotifications("contact");
  const [messages] = useNotifications("message");
  const notificationsMap: Record<string, WsNotification[]> = {
    order: orders,
    contact: contacts,
    message: messages,
  };

  return (
    <DesktopSidebarShell
      sidebarItems={sidebarItems}
      pathname={pathname}
      user={user}
      t={t}
      notificationsMap={notificationsMap}
      isLoading={isSidebarLoading}
    >
      {children}
    </DesktopSidebarShell>
  );
}

function DesktopSidebarShell({
  children,
  sidebarItems,
  pathname,
  user,
  t,
  notificationsMap,
  isLoading,
}: {
  children: React.ReactNode;
  sidebarItems: SidebarItem[];
  pathname: string;
  user: any;
  t: any;
  notificationsMap: Record<string, WsNotification[]>;
  isLoading?: boolean;
}) {
  const { width, startResize } = useSidebarWidth();
  return (
    <SidebarProvider
      defaultOpen={false}
      style={{ "--sidebar-width": `${width}px` } as React.CSSProperties}
    >
      <SidebarContentWrapper
        sidebarItems={sidebarItems}
        pathname={pathname}
        user={user}
        t={t}
        notificationsMap={notificationsMap}
        isLoading={isLoading}
        startResize={startResize}
      >
        {children}
      </SidebarContentWrapper>
    </SidebarProvider>
  );
}
